import { useState } from 'react';
import { colors, Card, Button, LoadingSpinner } from '../../shared';
import { ArrowLeft, Save, Plus, Trash2, Play, Code, FileText, Info } from 'lucide-react';

const MODELS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (fast, cheap)' },
  { value: 'gpt-4o', label: 'GPT-4o (vision, complex)' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
];

const PRODUCTS = [
  { value: 'trades-app', label: 'Trades App' },
  { value: 'insurance-estimator', label: 'Insurance Estimator' },
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'testing', label: 'Testing' },
  { value: 'deprecated', label: 'Deprecated' },
];

const OUTPUT_FORMATS = [
  { value: 'text', label: 'Plain Text' },
  { value: 'json', label: 'JSON' },
];

const TemplateVariableEditor = ({ variables, onChange }) => {
  const addVariable = () => {
    onChange([...variables, { name: '', type: 'string', description: '' }]);
  };

  const updateVariable = (index, field, value) => {
    const updated = [...variables];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeVariable = (index) => {
    onChange(variables.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium" style={{ color: colors.textSecondary }}>
          Template Variables
        </label>
        <button
          onClick={addVariable}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded"
          style={{ background: colors.yellowDim, color: colors.yellow }}
        >
          <Plus size={12} />
          Add Variable
        </button>
      </div>

      {variables.length === 0 ? (
        <p className="text-xs p-3 rounded" style={{ background: colors.inputBg, color: colors.textMuted }}>
          No variables defined. Use {'{{variableName}}'} in your template.
        </p>
      ) : (
        <div className="space-y-2">
          {variables.map((variable, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 p-2 rounded"
              style={{ background: colors.inputBg }}
            >
              <input
                type="text"
                value={variable.name}
                onChange={(e) => updateVariable(idx, 'name', e.target.value)}
                placeholder="variableName"
                className="flex-1 px-2 py-1 rounded text-sm outline-none"
                style={{ background: colors.cardBg, color: colors.textPrimary }}
              />
              <select
                value={variable.type}
                onChange={(e) => updateVariable(idx, 'type', e.target.value)}
                className="px-2 py-1 rounded text-sm outline-none"
                style={{ background: colors.cardBg, color: colors.textPrimary }}
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="array">Array</option>
                <option value="object">Object</option>
              </select>
              <input
                type="text"
                value={variable.description}
                onChange={(e) => updateVariable(idx, 'description', e.target.value)}
                placeholder="Description"
                className="flex-1 px-2 py-1 rounded text-sm outline-none"
                style={{ background: colors.cardBg, color: colors.textPrimary }}
              />
              <button
                onClick={() => removeVariable(idx)}
                className="p-1 rounded"
                style={{ color: colors.red }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function PromptEditor({
  prompt,
  isNew,
  onSave,
  onCancel,
  onCreateVersion,
  canEdit,
  canDeploy,
}) {
  const [formData, setFormData] = useState({
    id: prompt.id || '',
    name: prompt.name || '',
    product: prompt.product || 'trades-app',
    systemPrompt: prompt.systemPrompt || '',
    userPromptTemplate: prompt.userPromptTemplate || '',
    templateVariables: prompt.templateVariables || [],
    model: prompt.model || 'gpt-4o-mini',
    maxTokens: prompt.maxTokens || 1000,
    temperature: prompt.temperature || 0.7,
    outputFormat: prompt.outputFormat || 'text',
    jsonSchema: prompt.jsonSchema || null,
    status: prompt.status || 'testing',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [changeNotes, setChangeNotes] = useState('');
  const [showVersionDialog, setShowVersionDialog] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.id.trim()) {
      setError('Prompt ID is required');
      return;
    }
    if (!formData.name.trim()) {
      setError('Prompt name is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave(formData, isNew);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!changeNotes.trim()) {
      setError('Please add change notes');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onCreateVersion(formData.id, {
        systemPrompt: formData.systemPrompt,
        userPromptTemplate: formData.userPromptTemplate,
        model: formData.model,
        maxTokens: formData.maxTokens,
        temperature: formData.temperature,
        changeNotes,
        createdBy: 'admin', // Would use actual user email
      });
      setShowVersionDialog(false);
      setChangeNotes('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const readOnly = !canEdit;

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="p-2 rounded-lg"
            style={{ background: colors.cardBg, color: colors.textMuted }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
              {isNew ? 'New Prompt' : formData.name}
            </h1>
            {!isNew && (
              <p className="text-sm font-mono" style={{ color: colors.textMuted }}>
                v{prompt.currentVersion || 1}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isNew && canDeploy && (
            <Button
              variant="secondary"
              onClick={() => setShowVersionDialog(true)}
              disabled={saving}
            >
              Create New Version
            </Button>
          )}
          {canEdit && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <LoadingSpinner size="sm" color={colors.black} /> : <Save size={18} />}
              {isNew ? 'Create' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg" style={{ background: colors.redDim, color: colors.red }}>
          {error}
        </div>
      )}

      {/* Form */}
      <div className="space-y-6">
        {/* Basic Info */}
        <Card>
          <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textPrimary }}>
            Basic Information
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Prompt ID
              </label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => handleChange('id', e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                placeholder="generateWOBriefing"
                disabled={!isNew || readOnly}
                className="w-full px-4 py-2 rounded-xl outline-none disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                Unique identifier (letters, numbers, underscores, hyphens)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Display Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Work Order Briefing Generator"
                disabled={readOnly}
                className="w-full px-4 py-2 rounded-xl outline-none disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Product
              </label>
              <select
                value={formData.product}
                onChange={(e) => handleChange('product', e.target.value)}
                disabled={readOnly}
                className="w-full px-4 py-2 rounded-xl outline-none disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              >
                {PRODUCTS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                disabled={readOnly}
                className="w-full px-4 py-2 rounded-xl outline-none disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              >
                {STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Prompts */}
        <Card>
          <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textPrimary }}>
            Prompt Templates
          </h2>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              System Prompt
            </label>
            <textarea
              value={formData.systemPrompt}
              onChange={(e) => handleChange('systemPrompt', e.target.value)}
              placeholder="You are a helpful maintenance assistant..."
              disabled={readOnly}
              rows={4}
              className="w-full px-4 py-3 rounded-xl outline-none font-mono text-sm disabled:opacity-50"
              style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              User Prompt Template
            </label>
            <textarea
              value={formData.userPromptTemplate}
              onChange={(e) => handleChange('userPromptTemplate', e.target.value)}
              placeholder="Given the work order {{workOrder}}, provide a briefing..."
              disabled={readOnly}
              rows={8}
              className="w-full px-4 py-3 rounded-xl outline-none font-mono text-sm disabled:opacity-50"
              style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
            />
            <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
              Use {'{{variableName}}'} for dynamic values
            </p>
          </div>

          <TemplateVariableEditor
            variables={formData.templateVariables}
            onChange={(vars) => handleChange('templateVariables', vars)}
          />
        </Card>

        {/* Model Configuration */}
        <Card>
          <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textPrimary }}>
            Model Configuration
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Model
              </label>
              <select
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                disabled={readOnly}
                className="w-full px-4 py-2 rounded-xl outline-none disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              >
                {MODELS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Output Format
              </label>
              <select
                value={formData.outputFormat}
                onChange={(e) => handleChange('outputFormat', e.target.value)}
                disabled={readOnly}
                className="w-full px-4 py-2 rounded-xl outline-none disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              >
                {OUTPUT_FORMATS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Max Tokens
              </label>
              <input
                type="number"
                value={formData.maxTokens}
                onChange={(e) => handleChange('maxTokens', parseInt(e.target.value) || 1000)}
                min={100}
                max={8000}
                disabled={readOnly}
                className="w-full px-4 py-2 rounded-xl outline-none disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Temperature ({formData.temperature})
              </label>
              <input
                type="range"
                value={formData.temperature}
                onChange={(e) => handleChange('temperature', parseFloat(e.target.value))}
                min={0}
                max={1}
                step={0.1}
                disabled={readOnly}
                className="w-full"
              />
              <div className="flex justify-between text-xs" style={{ color: colors.textMuted }}>
                <span>Precise (0)</span>
                <span>Creative (1)</span>
              </div>
            </div>
          </div>

          {formData.outputFormat === 'json' && (
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                JSON Schema (optional)
              </label>
              <textarea
                value={formData.jsonSchema ? JSON.stringify(formData.jsonSchema, null, 2) : ''}
                onChange={(e) => {
                  try {
                    handleChange('jsonSchema', e.target.value ? JSON.parse(e.target.value) : null);
                  } catch {}
                }}
                placeholder='{"type": "object", "properties": {...}}'
                disabled={readOnly}
                rows={6}
                className="w-full px-4 py-3 rounded-xl outline-none font-mono text-sm disabled:opacity-50"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
            </div>
          )}
        </Card>
      </div>

      {/* Version Dialog */}
      {showVersionDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
          <Card className="w-full max-w-md">
            <h2 className="text-lg font-bold mb-4" style={{ color: colors.textPrimary }}>
              Create New Version
            </h2>
            <p className="text-sm mb-4" style={{ color: colors.textSecondary }}>
              This will create version {(prompt.currentVersion || 1) + 1} of this prompt.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Change Notes
              </label>
              <textarea
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                placeholder="Describe what changed in this version..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleCreateVersion} disabled={saving || !changeNotes.trim()}>
                {saving ? 'Creating...' : 'Create Version'}
              </Button>
              <Button variant="secondary" onClick={() => setShowVersionDialog(false)}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
